
import { defineComponent, ref, computed } from "vue";
import axios from "@/axios-instance";
import moment from "moment";
import { sortByAlphabet } from "@/core/helpers/sortHelpers";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

export default defineComponent({
  name: "scholarship-register-drawer",
  emits: ["saved"],
  setup(props, { emit }) {
    const visible = ref(false);
    const loading = ref(false);
    const saving = ref(false);
    const isEditing = ref(false);
    const d = ref<any>({});
    const editForm = ref<any>({ regScholarshipId: "", majorId: "" });

    const badgeStatus: any = {
      new: { label: "Chờ cấp", badge: "warning" },
      pending: { label: "Chờ phê duyệt", badge: "primary" },
      approved: { label: "Đã duyệt", badge: "success" },
      rejected: { label: "Từ chối", badge: "danger" },
    };
    // Giống checkDisabled của page cũ: approved/rejected = chỉ xem
    const canEdit = computed(() => d.value.status != "approved" && d.value.status != "rejected");

    // HBĐK: danh mục học bổng public (nạp 1 lần)
    const scholarshipOptions = ref<any[]>([]);
    let optionsLoaded = false;
    const loadOptions = async () => {
      if (optionsLoaded) return;
      optionsLoaded = true;
      await axios.get(`/setting/scholarship_public/find/findall`)
        .then(({ data }) => { scholarshipOptions.value = sortByAlphabet(data, "name") || []; })
        .catch(() => {});
    };

    // Ngành: gộp mọi ngành từ các hồ sơ ĐKXT của ứng viên (mỗi ngành kèm MHS/admissionRegisterId) — theo từng ứng viên
    const majorOptions = ref<any[]>([]);
    const loadMajors = async (studentId: any) => {
      majorOptions.value = [];
      if (!studentId) return;
      await axios.get(`/admission/register/student/${studentId}`)
        .then(({ data }) => {
          const all: any[] = [];
          const seen = new Set();
          (data || []).forEach((adReg: any) => {
            (adReg.majors || []).forEach((m: any) => {
              if (seen.has(m.id)) return;
              seen.add(m.id);
              all.push({ id: m.id, code: m.code, name: m.name, profileCode: adReg.profileCode, admissionRegisterId: adReg.id, priority: m.priority });
            });
          });
          majorOptions.value = all;
        })
        .catch(() => {});
    };

    // Mở drawer: mode 'view' (mặc định) | 'edit' (vào thẳng chế độ sửa)
    const open = async (id: string | number, mode: "view" | "edit" = "view") => {
      visible.value = true;
      loading.value = true;
      isEditing.value = false;
      d.value = {};
      adRegInfo.value = null;
      loadUsers(); // map updatedBy -> tên (truy vết người duyệt/từ chối) — không cần chờ
      await loadOptions();
      await axios.get(`/scholarshipregisters/${id}?select=Student,AdmissionRegister,Scholarship`)
        .then(({ data }) => { d.value = data || {}; })
        .catch(() => { d.value = {}; });
      loading.value = false;
      loadAdRegInfo(); // hồ sơ xét tuyển (ngành/hình thức/điểm) — không chặn hiển thị drawer
      if (mode === "edit" && canEdit.value) await startEdit();
    };

    // Khối "Hồ sơ xét tuyển": ngành theo NV + hình thức xét (adForm) + điểm xét (averageScore) —
    // leader duyệt dùm không mở được trang ứng viên người khác nên cần đủ thông tin ngay trong drawer
    const adRegInfo = ref<any>(null);
    const loadAdRegInfo = async () => {
      if (!d.value.studentId) return;
      await axios.get(`/admission/register/student/${d.value.studentId}`)
        .then(({ data }) => {
          const regs = Array.isArray(data) ? data : [];
          const reg = regs.find((r: any) => String(r.id) === String(d.value.admissionRegisterId))
            || regs.find((r: any) => r.profileCode && r.profileCode === d.value.admissionRegisterCode);
          if (!reg) return;
          adRegInfo.value = {
            profileCode: reg.profileCode,
            method: reg.adMethodName,
            majors: (reg.majors || []).slice().sort((a: any, b: any) => (a.priority ?? 99) - (b.priority ?? 99)),
          };
        })
        .catch(() => { adRegInfo.value = null; });
    };

    const startEdit = async () => {
      await loadMajors(d.value.studentId);
      await loadOptions();
      // HB đang đăng ký không nằm trong danh mục public (bị ẩn/hết hạn) -> chèn option từ record
      // để select hiện TÊN thay vì trơ số id; field `type` = code cho khớp payload save.
      if (d.value.regScholarshipId && !scholarshipOptions.value.find((i: any) => i.id == d.value.regScholarshipId)) {
        scholarshipOptions.value.unshift({
          id: d.value.regScholarshipId,
          name: d.value.regScholarshipName || `Học bổng #${d.value.regScholarshipId}`,
          type: d.value.regScholarshipCode || "",
        });
      }
      editForm.value = {
        regScholarshipId: d.value.regScholarshipId || "",
        majorId: d.value.majorId || "",
      };
      isEditing.value = true;
    };
    const cancelEdit = () => { isEditing.value = false; };

    const save = async () => {
      const recordMajor = majorOptions.value.find((i: any) => i.id == editForm.value.majorId) || {};
      const recordScholarship = scholarshipOptions.value.find((i: any) => i.id == editForm.value.regScholarshipId) || {};
      saving.value = true;
      // Payload đồng bộ với submit của registerScholarshipAdd (PUT /scholarshipregisters/:id)
      const data = {
        admissionRegisterId: recordMajor.admissionRegisterId,
        admissionRegisterCode: recordMajor.profileCode,
        studentId: d.value.studentId,
        studentName: d.value.studentName,
        regScholarshipId: recordScholarship.id,
        regScholarshipName: recordScholarship.name,
        regScholarshipCode: recordScholarship.type,
        status: d.value.status || "new",
        majorCode: recordMajor.code,
        majorId: recordMajor.id,
        majorName: recordMajor.name,
        id: d.value.id,
      };
      await axios.put(`/scholarshipregisters/${d.value.id}`, data)
        .then(() => {
          d.value = { ...d.value, ...data };
          isEditing.value = false;
          visible.value = false; // lưu xong tự đóng drawer (sửa nhanh từ list)
          emit("saved");
          Swal.fire({
            text: "Đã lưu thành công!",
            icon: "success",
            buttonsStyling: false,
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        })
        .catch(({ response }) => {
          Swal.fire({
            // gọt tiền tố kỹ thuật "Validation error: " do Sequelize bọc (message gốc BE đã là tiếng Việt)
            text: String(response?.data?.errorMessage || "").replace(/^Validation error:\s*/i, "") || "Có lỗi xảy ra, vui lòng thử lại.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Đóng",
            customClass: { confirmButton: "btn btn-primary" },
          });
        });
      saving.value = false;
    };

    const fmtDate = (v: any) => (v ? moment(v).format("DD/MM/YYYY") : "—");
    const fmtDateTime = (v: any) => (v ? moment(v).format("DD/MM/YYYY HH:mm") : "—");

    // Dòng grant đầu (ScholarshipStudents luôn được include ở findById) — fallback cho register cũ thiếu majorName/issuedScholarshipName
    const grant = computed(() => (d.value.ScholarshipStudents && d.value.ScholarshipStudents[0]) || {});
    // Lý do từ chối: otherInfo là object {rejectReason} khi Từ chối (mảng học bổng-cấp khi Duyệt)
    const rejectReason = computed(() =>
      d.value.otherInfo && !Array.isArray(d.value.otherInfo) ? d.value.otherInfo.rejectReason : ""
    );
    // Truy vết người duyệt/từ chối: updatedBy (UUID user) của lần cập nhật cuối — duyệt/từ chối khoá sửa nên đó chính là hành động duyệt
    const usersById = ref<any>({});
    let usersLoaded = false;
    const loadUsers = async () => {
      if (usersLoaded) return;
      usersLoaded = true;
      await axios.get(`/user/findusersforselect`)
        .then(({ data }) => {
          const map: any = {};
          (data || []).forEach((u: any) => { map[u.id] = u.fullName || u.email; });
          usersById.value = map;
        })
        .catch(() => { usersLoaded = false; });
    };
    const actorName = computed(() => usersById.value[d.value.updatedBy] || "");
    // Người DUYỆT: approvedBy (bất biến); record trước khi có cột approved_* -> fallback updatedBy
    const approverName = computed(() => usersById.value[d.value.approvedBy || d.value.updatedBy] || "");
    // Có sửa quyết định SAU khi duyệt? (khác người, hoặc updatedAt lệch approvedAt > 1 phút)
    const editedAfterApprove = computed(() => {
      if (!d.value.approvedAt || !d.value.updatedAt) return false;
      if (d.value.approvedBy && d.value.updatedBy && d.value.approvedBy != d.value.updatedBy) return true;
      return new Date(d.value.updatedAt).getTime() - new Date(d.value.approvedAt).getTime() > 60 * 1000;
    });

    // Sửa thông tin QUYẾT ĐỊNH cấp (Số/Ngày thông báo, Đợt xét) — cập nhật register + ĐỒNG BỘ mọi dòng grant
    // cùng register (DS nhận học bổng đọc Số/Ngày từ grant — không sync là 2 nơi lệch nhau)
    const editingIssued = ref(false);
    const savingIssued = ref(false);
    const issuedForm = ref<any>({ issuedNo: "", issuedDate: null, issuedPhase: "" });
    const startEditIssued = () => {
      const g = grant.value;
      issuedForm.value = {
        issuedNo: d.value.issuedNo || g.issuedNo || "",
        issuedDate: d.value.issuedDate || g.issuedDate || null,
        issuedPhase: d.value.issuedPhase || g.issuedPhase || "",
      };
      editingIssued.value = true;
    };
    const saveIssued = async () => {
      savingIssued.value = true;
      const payload = {
        issuedNo: issuedForm.value.issuedNo,
        issuedDate: issuedForm.value.issuedDate,
        issuedPhase: issuedForm.value.issuedPhase,
      };
      try {
        await axios.put(`/scholarshipregisters/${d.value.id}`, payload);
        const grants = d.value.ScholarshipStudents || [];
        await Promise.all(grants.map((g: any) => axios.put(`/scholarship/student/${g.id}`, payload).catch(() => null)));
        // Cập nhật truy vết tại chỗ (không cần refetch): người sửa cuối = user hiện tại
        let _uid = "";
        try { _uid = JSON.parse(window.localStorage.getItem("user") || "{}").id || ""; } catch (e) { /* ignore */ }
        d.value = { ...d.value, ...payload, updatedAt: new Date().toISOString(), updatedBy: _uid || d.value.updatedBy };
        (d.value.ScholarshipStudents || []).forEach((g: any) => Object.assign(g, payload));
        editingIssued.value = false;
        emit("saved");
        Swal.fire({ text: "Đã cập nhật thông tin quyết định!", icon: "success", buttonsStyling: false, timer: 1500, timerProgressBar: true, showConfirmButton: false });
      } catch (e: any) {
        Swal.fire({ text: String(e?.response?.data?.errorMessage || "").replace(/^Validation error:\s*/i, "") || "Có lỗi xảy ra, vui lòng thử lại.", icon: "error", buttonsStyling: false, confirmButtonText: "Đóng", customClass: { confirmButton: "btn btn-primary" } });
      }
      savingIssued.value = false;
    };

    return { visible, loading, saving, isEditing, d, editForm, badgeStatus, canEdit, scholarshipOptions, majorOptions, open, startEdit, cancelEdit, save, fmtDate, fmtDateTime, grant, rejectReason, actorName, approverName, editedAfterApprove, loadUsers, editingIssued, savingIssued, issuedForm, startEditIssued, saveIssued, adRegInfo };
  },
});
